// Visa Portals Console — coverage of official visa/immigration portals across
// the directory. Kit-based, interactive, cross-linked to Directory + the editor.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, Donut, Table, Drawer, Empty, ExportButton, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';
import { setFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());

function useVisa() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_visa_overview').then(({ data }) => { if (!c) setD(data || {}); }); return () => { c = true; }; }, []);
  return d;
}

function PortalInspector({ row, onNav }) {
  const { t } = useConsoleTheme();
  return (
    <div>
      <div style={{ fontSize:'1.3rem', fontWeight:500, color:t.tx }}>{row.portal || '—'}</div>
      <div style={{ fontSize:'0.85rem', color:t.mut, marginBottom:12 }}>{row.country}</div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
        {row.category && <span style={{ fontSize:'0.82rem', padding:'2px 9px', borderRadius:8, background:t.accent+'22', color:t.accent, fontWeight:600 }}>{row.category}</span>}
        {row.visa_type && <span style={{ fontSize:'0.82rem', padding:'2px 9px', borderRadius:8, background:t.gbar, color:t.mut, fontWeight:600 }}>{row.visa_type}</span>}
      </div>
      {row.url ? (
        <a href={row.url} target="_blank" rel="noreferrer" style={{ display:'inline-block', fontSize:'0.9rem', color:t.search, wordBreak:'break-all', marginBottom:14 }}>{row.url} ↗</a>
      ) : <Empty>No portal URL on record.</Empty>}
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <button onClick={() => { setFocus({ type:'country', value:row.country }); onNav && onNav('directory'); }}
        style={{ fontSize:'0.85rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500, marginRight:8 }}>
        {row.country} in Directory →
      </button>
    </div>
  );
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const d = useVisa();
  const [sel, setSel] = useState(null);
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);

  const cats = (d && d.by_category) || [];
  const catDonut = cats.map((x, i) => ({ label: x.k, value: x.n, color: PALETTE[i % PALETTE.length] }));
  const countries = (d && d.by_country) || [];
  const maxC = Math.max(1, ...countries.map(x => x.n));
  const types = (d && d.by_type) || [];
  const maxT = Math.max(1, ...types.map(x => x.n));

  const rows = (d && d.rows) || [];
  const cols = [
    { label:'Country', key:'country', sort:r => r.country, render:r => r.country },
    { label:'Portal', key:'portal', render:r => r.portal || '—' },
    { label:'Category', key:'category', render:r => r.category || '—' },
    { label:'Visa type', key:'visa_type', muted:true, render:r => r.visa_type || '—' },
    { label:'URL', align:'right', render:r => r.url ? <span style={{ color:t.search }}>link ↗</span> : <span style={{ color:t.mut }}>—</span>, text:r => r.url || '' },
  ];

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(4), gap:10 }}>
        <KPI label="Visa portals" value={fmt(d && d.total)} sub="official sources" accent="#6F8FA7" />
        <KPI label="Countries" value={fmt(d && d.countries)} sub="covered" accent={t.accent} />
        <KPI label="Categories" value={fmt(d && d.categories)} sub="visa classes" accent="#819D84" />
        <KPI label="With URL" value={fmt(d && d.with_url)} sub={d && d.total ? `${Math.round((d.with_url / d.total) * 100)}% linked` : ''} subColor={t.up} accent="#9F7E4F" />
      </div>

      <SectionLabel right="click a country to compare">Coverage</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ By category">
          {!d ? <Empty>Loading…</Empty> : cats.length === 0 ? <Empty>No portals yet.</Empty>
            : <Donut data={catDonut} center={{ value: fmt(d.total), label: 'portals' }} />}
        </Panel>
        <Panel title="◷ Top countries" right="by portal count">
          {!d ? <Empty>Loading…</Empty> : countries.length === 0 ? <Empty>No country data yet.</Empty>
            : countries.map(x => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxC * 100)} labelWidth={130}
                onClick={() => { setFocus({ type:'country', value:x.k }); onNav && onNav('directory'); }}
                hint={`${x.k} — ${fmt(x.n)} portal${x.n === 1 ? '' : 's'} · open in Directory`} />)}
        </Panel>
      </div>

      <SectionLabel>Visa types</SectionLabel>
      <Panel title="◇ Most common visa types">
        {!d ? <Empty>Loading…</Empty> : types.length === 0 ? <Empty>No type data yet.</Empty>
          : <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:'0 26px' }}>
              {types.map((x, i) => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxT * 100)} color={PALETTE[i % PALETTE.length]} labelWidth={130} />)}
            </div>}
      </Panel>

      <SectionLabel right="click a row to inspect">All portals</SectionLabel>
      <Panel title="◈ Portal directory" right={<ExportButton filename="sanoire-visa-portals" cols={cols} rows={rows} />}>
        {!d ? <Empty>Loading portals…</Empty> : <Table empty="No visa portals yet." cols={cols} rows={rows} onRowClick={r => setSel(r)} pageSize={20} />}
      </Panel>

      <div style={{ marginTop:14 }}>
        <button onClick={() => onNav && onNav('visaportalseditor')}
          style={{ fontSize:'0.9rem', padding:'8px 14px', borderRadius:8, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>
          ✎ Open Visa Portals editor →
        </button>
      </div>

      <Drawer open={!!sel} onClose={() => setSel(null)} title="Visa portal">
        {sel && <PortalInspector row={sel} onNav={onNav} />}
      </Drawer>
    </div>
  );
}

export default function VisaConsole(props) {
  return <ConsoleShell title="Visa Portals Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
